import React, { useEffect, useMemo, useRef, useState } from "react";
import { Animated, Easing, StyleSheet, View } from "react-native";
import { CheckCircleIcon, WarningCircleIcon } from "@phosphor-icons/react";
import { Loader2 } from "lucide-react";

import CustomText from "../../../common/general/CustomText/CustomText";
import { RubricParseStatus, type ActiveRubricSetQuery, type RubricSetQuery } from "@axio/graphql/generated/graphql";

type ActiveRubricViewModel = NonNullable<ActiveRubricSetQuery["activeRubricSet"]["data"]>;
type RubricSetViewModel = NonNullable<RubricSetQuery["rubricSet"]["data"]>;

interface RubricProcessingViewModel {
  parseStatus: RubricParseStatus;
  parseError?: string | null;
  sections?: ActiveRubricViewModel["sections"] | RubricSetViewModel["sections"];
  sourceFile?: {
    fileName: string;
  } | null;
}

interface RubricProcessingProps {
  rubric: RubricProcessingViewModel | null;
  selectedFileName?: string | null;
}

const parseStatusLabels: Record<RubricParseStatus, string> = {
  [RubricParseStatus.Failed]: "Parsing failed",
  [RubricParseStatus.Parsing]: "Parsing rubric",
  [RubricParseStatus.Pending]: "Queued for parsing",
  [RubricParseStatus.Ready]: "Ready",
};

const progressSteps = [
  "Parsing rubric document...",
  "Identifying evaluation categories...",
  "Extracting standards and criteria...",
  "Building scoring model...",
];

const sumMaxScore = (sections: NonNullable<RubricProcessingViewModel["sections"]>) =>
  sections.reduce(
    (sectionTotal, section) =>
      sectionTotal +
      section.criteria.reduce((criterionTotal, criterion) => criterionTotal + (criterion.maxScore ?? 0), 0),
    0
  );

function SpinningLoader() {
  const spinAnim = useRef(new Animated.Value(0)).current;
  const spinnerRotation = spinAnim.interpolate({
    inputRange: [0, 1],
    outputRange: ["0deg", "360deg"],
  });

  useEffect(() => {
    spinAnim.setValue(0);
    const loop = Animated.loop(
      Animated.timing(spinAnim, {
        toValue: 1,
        duration: 1000,
        easing: Easing.linear,
        useNativeDriver: true,
      })
    );
    loop.start();

    return () => loop.stop();
  }, [spinAnim]);

  return (
    <Animated.View style={{ transform: [{ rotate: spinnerRotation }] }}>
      <Loader2 color="#3E68FE" size={18} />
    </Animated.View>
  );
}

function RubricProcessing({ rubric }: RubricProcessingProps) {
  const [animatedStep, setAnimatedStep] = useState(0);
  const parseStatus = rubric?.parseStatus ?? RubricParseStatus.Pending;
  const isFailed = parseStatus === RubricParseStatus.Failed;
  const isReady = parseStatus === RubricParseStatus.Ready;
  const sections = rubric?.sections ?? [];
  const totalMaxScore = useMemo(() => sumMaxScore(sections), [sections]);
  const currentStep = isReady ? progressSteps.length : Math.min(animatedStep, progressSteps.length - 1);

  useEffect(() => {
    if (isReady || isFailed) {
      return undefined;
    }

    const interval = setInterval(() => {
      setAnimatedStep((previousStep) => Math.min(previousStep + 1, progressSteps.length - 1));
    }, 1200);

    return () => clearInterval(interval);
  }, [isFailed, isReady]);

  return (
    <View style={isFailed ? styles.failedPanel : styles.processingPanel}>
      {isFailed || isReady ? (
        <View style={styles.processingHeader}>
          {isFailed ? (
            <WarningCircleIcon color="#B42318" size={28} weight="bold" />
          ) : (
            <CheckCircleIcon color="#027A48" size={28} weight="fill" />
          )}
          <View style={styles.headerCopy}>
            <CustomText
              text={isReady ? "Rubric Extracted" : parseStatusLabels[parseStatus]}
              size="m"
              weight="bold"
              style={isFailed ? styles.errorText : styles.primaryText}
              useTranslationText={false}
            />
            <CustomText
              text={
                isFailed
                  ? rubric?.parseError || "The rubric could not be parsed. Select another file and try again."
                  : "We extracted the following categories and standards from your document."
              }
              size="s"
              style={isFailed ? styles.errorText : styles.secondaryText}
              useTranslationText={false}
            />
          </View>
        </View>
      ) : null}

      {!isFailed ? (
        <View style={styles.stepsList}>
          {progressSteps.map((step, stepIndex) => {
            const stepDone = stepIndex < currentStep || isReady;
            const stepActive = stepIndex === currentStep && !isReady;

            return (
              <View key={step} style={styles.stepRow}>
                {stepDone ? <CheckCircleIcon color="#027A48" size={18} weight="fill" /> : <SpinningLoader />}
                <CustomText
                  text={step}
                  size="s"
                  weight={stepActive ? "medium" : "regular"}
                  style={stepDone ? styles.successText : stepActive ? styles.primaryText : styles.mutedText}
                  useTranslationText={false}
                />
              </View>
            );
          })}
        </View>
      ) : null}

      {isReady && sections.length > 0 ? (
        <View style={styles.previewPanel}>
          <CustomText
            text={`Extracted ${sections.length} categories${totalMaxScore > 0 ? ` - ${totalMaxScore} points total` : ""}`}
            size="xs"
            weight="bold"
            style={styles.sectionEyebrow}
            useTranslationText={false}
          />
          <View style={styles.previewList}>
            {sections.map((section) => (
              <View key={section.id} style={styles.previewCard}>
                <CustomText
                  text={section.title}
                  size="xs"
                  weight="medium"
                  style={styles.primaryText}
                  useTranslationText={false}
                />
                <CustomText
                  text={`${section.criteria.length} ${section.criteria.length === 1 ? "standard" : "standards"}`}
                  size="xs"
                  style={styles.successText}
                  useTranslationText={false}
                />
              </View>
            ))}
          </View>
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  primaryText: {
    color: "#101828",
  },
  secondaryText: {
    color: "#344054",
  },
  mutedText: {
    color: "#667085",
  },
  successText: {
    color: "#027A48",
  },
  errorText: {
    color: "#B42318",
  },
  sectionEyebrow: {
    color: "#344054",
  },
  processingPanel: {
    gap: 20,
    paddingHorizontal: 0,
    paddingVertical: 4,
    borderRadius: 8,
    backgroundColor: "#FFFFFF",
  },
  failedPanel: {
    gap: 10,
    padding: 20,
    borderRadius: 8,
    backgroundColor: "#FEF3F2",
    borderWidth: 1,
    borderColor: "#FECDCA",
    marginBottom: 16,
  },
  processingHeader: {
    flexDirection: "row",
    gap: 12,
    alignItems: "flex-start",
  },
  headerCopy: {
    flex: 1,
    gap: 4,
  },
  stepsList: {
    gap: 14,
    paddingVertical: 4,
  },
  stepRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  previewPanel: {
    gap: 12,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: "#EAECF0",
  },
  previewList: {
    gap: 8,
  },
  previewCard: {
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 12,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#ABEFC6",
    backgroundColor: "#ECFDF3",
  },
});

export default RubricProcessing;
